import { useEffect, useMemo, useState } from 'react';
import { Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import api from '@/lib/api';
import { useLang } from '@/contexts/LangContext';

function sanitizePhone(phone) {
  return String(phone || '').replace(/[^\d]/g, '');
}

export const QuickContactBar = () => {
  const { t, lang } = useLang();
  const [contactInfo, setContactInfo] = useState(null);
  const isArabic = String(lang || '').toLowerCase().startsWith('ar');
  const isRightSide = isArabic;
  const desktopSideClass = isRightSide ? 'right-4 items-end' : 'left-4 items-start';
  const mobileSideClass = 'left-1/2 -translate-x-1/2';
  const labelHiddenClass = isRightSide ? 'translate-x-3' : '-translate-x-3';

  useEffect(() => {
    api.get('/content/contact-info').then((res) => setContactInfo(res.data || {})).catch(() => {});
  }, [lang]);

  const items = useMemo(() => {
    if (!contactInfo) return [];
    const phone = contactInfo.phone || '';
    const whatsappNumber = sanitizePhone(phone);
    return [
      phone ? { key: 'phone', label: t('contact.call'), href: `tel:${phone}`, icon: Phone } : null,
      whatsappNumber ? { key: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/${whatsappNumber}`, icon: MessageCircle, external: true } : null,
      contactInfo.email ? { key: 'email', label: t('contact.email_us'), href: `mailto:${contactInfo.email}`, icon: Mail } : null,
      contactInfo.map_url ? { key: 'map', label: t('contact.visit'), href: contactInfo.map_url, icon: MapPin, external: true } : null,
    ].filter(Boolean);
  }, [contactInfo, t]);

  if (items.length === 0) return null;

  return (
    <>
      <div dir="ltr" className={`bf-quick-contact-stack fixed top-1/2 z-40 hidden -translate-y-1/2 flex-col gap-2.5 md:flex ${desktopSideClass}`}>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.key}
              dir="ltr"
              href={item.href}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
              className="bf-quick-contact-link bf-hover-lift group flex items-center gap-0 overflow-hidden rounded-full p-1.5 text-sm font-semibold text-white"
              title={item.label}
              aria-label={item.label}
            >
              {isRightSide && (
                <span
                  dir="rtl"
                  className={`inline-flex max-w-0 items-center justify-end overflow-hidden whitespace-nowrap text-right text-white/88 opacity-0 transition-all duration-300 group-hover:mr-4 group-hover:max-w-[180px] group-hover:opacity-100 group-hover:translate-x-0 ${labelHiddenClass}`}
                >
                  {item.label}
                </span>
              )}
              <span className="bf-quick-contact-icon flex h-10 w-10 items-center justify-center rounded-full text-white transition-transform duration-300 group-hover:scale-105">
                <Icon className="h-4 w-4" />
              </span>
              {!isRightSide && (
                <span
                  className={`inline-flex max-w-0 items-center justify-start overflow-hidden whitespace-nowrap text-left text-white/88 opacity-0 transition-all duration-300 group-hover:ml-4 group-hover:max-w-[180px] group-hover:opacity-100 group-hover:translate-x-0 ${labelHiddenClass}`}
                >
                  {item.label}
                </span>
              )}
            </a>
          );
        })}
      </div>

      <div className={`bf-quick-contact-mobile fixed bottom-4 z-40 flex items-center gap-2 rounded-full p-2 md:hidden ${mobileSideClass}`}>
        {items.slice(0, 4).map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.key}
              href={item.href}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
              className="bf-quick-contact-icon flex h-11 w-11 items-center justify-center rounded-full text-white transition-transform duration-300 active:scale-95"
              title={item.label}
            >
              <Icon className="h-4 w-4" />
            </a>
          );
        })}
      </div>
    </>
  );
};
